import React, { useMemo, useState } from "react";
import axios from "axios";
import { useStaticQuery, graphql } from "gatsby";
import styled, { css } from "styled-components";
import { Confirmation, FormField } from "./Form";
import { getGAClientId } from '../utils/getGAClientId';
import randomstring from "randomstring";

const ReviewFormWrapperStyles = styled.div`
  ${({ ...props }) => css`
    margin-top: 32px;
    max-width: 600px;
    h3 {
      margin-bottom: 24px;
    }
    ${(props) => {
      if (!props.$isGreyBg) {
        return `
          input,
          textarea,
          select {
            background-color: ${props.theme.colors.lightGrey};
          }
        `
      }
    }}
  `}
`;

const ButtonStyles = styled.button`
  ${({ ...props }) => css`
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.15), 0 2px 2px rgba(0, 0, 0, 0.15),
      0 4px 4px rgba(0, 0, 0, 0.15), 0 8px 8px rgba(0, 0, 0, 0.15);
    min-width: 200px;
  `}
`;

const FormFooterStyles = styled.div`
  ${({ ...props }) => css`
    h3 {
      margin-top: 24px;
    }
    a {
      color: ${props.theme.colors.primary};
    }
    @media (min-width: ${props.theme.breakpoints.md}) {
      margin-top: ${(props.$inline ? "24px" : "0px")};
      display: ${(props.$inline ? "flex" : "block")};
      justify-content: space-between;
      align-items: center;

      h3 {
        margin-top: ${(props.$inline ? "0" : "24px")};
      }
    }
  `}
`;

const RadioStyles = styled.div`
  ${({ ...props }) => css`
    /* Customize the label (the container) */
    > div {
      display: flex;
      align-items: center;
    }

    p {
      margin-bottom: 16px;
    }
    .container {
      display: block;
      position: relative;
      padding-left: 35px;
      cursor: pointer;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      line-height: 32px;
    }

    /* Hide the browser's default radio button */
    .container input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;
    }

    /* Create a custom radio button */
    .radiobtn {
      position: absolute;
      top: 0;
      left: 0;
      height: 25px;
      width: 25px;
      background-color: #fff;
      border-radius: 50%;
      transition: background-color .25s ease-in-out
    }

    ${(props) => {
      if (!props.$isGreyBg) {
        return `
          .radiobtn {
            background-color: ${props.theme.colors.lightGrey};
          }
        `
      }
    }}

    /* On mouse-over, add a grey background color */
    .container:hover input ~ .radiobtn {
      background-color: ${props.theme.colors.primary};
    }

    /* When the radio button is checked, add a blue background */
    .container input:checked ~ .radiobtn {
      background-color: ${props.theme.colors.primary};
    }

    /* Create the indicator (the dot/circle - hidden when not checked) */
    .radiobtn:after {
      content: "";
      position: absolute;
      display: none;
    }

    /* Show the indicator (dot/circle) when checked */
    .container input:checked ~ .radiobtn:after {
      display: block;
    }

    /* Style the indicator (dot/circle) */
    .container .radiobtn:after {
      top: 9px;
      left: 9px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: white;
    }
  `}
`;

export const ReviewForm = ({ source, productId, productName, isGreyBg = true }) => {
  const [serverState, setServerState] = useState({
    submitting: false,
    status: null,
  });
  let siteData = {
    site: {
      siteMetadata: {
        productReviewFormEndpoint: ''
      }
    }
  }
  if (!process.env.STORYBOOK) {
    siteData = useStaticQuery(graphql`
      query ReviewFormQuery {
        site {
          siteMetadata {
            productReviewFormEndpoint
          }
        }
      }
    `);
  }

  const handleServerResponse = (ok, msg, form) => {
    setServerState({
      submitting: false,
      status: { ok, msg },
    });
    if (ok) {
      form.reset();
    }
  };
  const handleOnSubmit = (e) => {
    e.preventDefault();
    const form = e.target;
    setServerState({ submitting: true });
    axios({
      method: "post",
      url: siteData.site.siteMetadata.productReviewFormEndpoint,
      data: new FormData(form),
    })
      .then((r) => {
        handleServerResponse(true, "Thank you for your review", form);
      })
      .catch((r) => {
        handleServerResponse(false, r.response.data.error, form);
      });
  };

  const rand = useMemo(() => randomstring.generate(5), []);

  return (
    <ReviewFormWrapperStyles
      $isGreyBg={isGreyBg}
    >
      <h3>Submit Review</h3>
      <form onSubmit={handleOnSubmit}>
        <input
          type="hidden"
          name="productName"
          id={`${rand}_productName`}
          value={productName}
          hidden
        />
        <input
          type="hidden"
          name="productId"
          id={`${rand}_productId`}
          value={productId}
          hidden
        />
        <input
          type="hidden"
          name="source"
          id={`${rand}_source`}
          value={source}
          hidden
        />
        <input
          type="hidden"
          name="googleClientId"
          id={`${rand}_googleClientId`}
          value={getGAClientId()}
          hidden
        />
        <FormField>
          <input
            type="text"
            name="name"
            id={`${rand}_name`}
            aria-describedby="name"
            placeholder=""
            required
          />
          <label htmlFor={`${rand}_name`}>Name/Company</label>
        </FormField>
        <FormField>
          <input
            type="text"
            name="jobReferenceNumber"
            id={`${rand}_jobReferenceNumber`}
            aria-describedby="jobReferenceNumber"
            placeholder=""
            required
          />
          <label htmlFor={`${rand}_jobReferenceNumber`}>Job Reference Number</label>
        </FormField>
        <FormField>
          <input
            type="text"
            name="salesPerson"
            id={`${rand}_salesPerson`}
            aria-describedby="salesPerson"
            placeholder=""
            required
          />
          <label htmlFor={`${rand}_salesPerson`}>Sales Person</label>
        </FormField>
        <FormField>
          <input
            type="text"
            name="Review title"
            id={`${rand}_reviewTitle`}
            aria-describedby="reviewTitle"
            placeholder=""
            required
          />
          <label htmlFor={`${rand}_reviewTitle`}>Review Title</label>
        </FormField>
        <FormField>
          <textarea
            type="text"
            name="review"
            id={`${rand}_Review`}
            aria-describedby="review"
            placeholder=""
            required
          />
          <label htmlFor={`${rand}_Review`}>Your Review</label>
        </FormField>
        <FormField>
          <RadioStyles
            $isGreyBg={isGreyBg}
          >
            <p>
              <strong>Rating:</strong>
            </p>
            <div>
              <label className="container">
                1
                <input type="radio" name="rating" value="1" />
                <span className="radiobtn"></span>
              </label>
              <label className="container">
                2
                <input type="radio" name="rating" value="2 " />
                <span className="radiobtn"></span>
              </label>
              <label className="container">
                3
                <input type="radio" name="rating" value="3" />
                <span className="radiobtn"></span>
              </label>
              <label className="container">
                4
                <input type="radio" name="rating" value="4" />
                <span className="radiobtn"></span>
              </label>
              <label className="container">
                5
                <input type="radio" defaultChecked="checked" name="rating" value="5" />
                <span className="radiobtn"></span>
              </label>
            </div>
          </RadioStyles>
        </FormField>
        <FormFooterStyles>
          <div>
            <ButtonStyles
              type="submit"
              disabled={serverState.submitting}
              id={`${rand}_review-submit-btn`}
            >
              Submit Review
            </ButtonStyles>
            <p className="no-js">Please enable Javascript to continue</p>
            {serverState.status && (
              <Confirmation>
                <p className={!serverState.status.ok ? "errorMsg" : ""}>
                  {serverState.status.msg}
                </p>
              </Confirmation>
            )}
          </div>
        </FormFooterStyles>
      </form>
    </ReviewFormWrapperStyles>
  );
};
